import React, { useState, useEffect } from 'react';
import { IconButton, Menu, MenuItem, useTheme, useMediaQuery, Slide, Dialog, Box } from '@mui/material';
import { useGetIdentity, useLogout, useTranslate } from 'react-admin';
import SettingsIcon from '@/assets/icons/settings-icon.svg?react';
import { styled } from '@mui/material/styles';
import LogoutIcon from '@/assets/icons/service-add-icon.svg?react';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NotificationSettings } from '@/pages/notificationSettings.jsx';
import { MyAccount } from '@/pages/myAccount.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useScrollLock } from '@/composables/useScrollLock';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction='left' ref={ref} {...props} />;
});

const CustomUserMenu = () => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { identity } = useGetIdentity();
    const logout = useLogout();
    const [anchorEl, setAnchorEl] = useState(null);
    const [shouldLockScroll, setShouldLockScroll] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();
    const isMenuOpen = Boolean(anchorEl);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (isMobile && isMenuOpen) {
            const timeout = setTimeout(() => {
                setShouldLockScroll(true);
            }, 100);
            return () => clearTimeout(timeout);
        } else {
            setShouldLockScroll(false);
        }
    }, [isMobile, isMenuOpen]);

    useScrollLock(shouldLockScroll);

    const handleLogout = () => {
        handleClose();
        logout();
    };

    const handleDialogClose = () => {
        navigate(location.pathname, { replace: true });
    };

    const modalContentMap = {
        '#user-account': MyAccount,
        '#notification-settings': NotificationSettings,
    };

    const isModalOpen = Object.keys(modalContentMap).includes(location.hash);

    const getModalContent = () => {
        if (location.hash) {
            const Component = modalContentMap[location.hash];
            return Component ? <Component handleClose={handleDialogClose} /> : null;
        }
        return null;
    };

    const handleOpenMyAccount = () => {
        handleClose();
        navigate('#user-account');
    };

    const handleOpenNotificationSettings = () => {
        handleClose();
        navigate('#notification-settings');
    };

    return (
        <>
            <StyledIconButton className={`user-menu ${anchorEl ? 'active' : ''}`} onClick={handleClick}>
                {isMdUp && <span className='user-name'>{identity?.fullName}</span>}
                <SettingsIcon />
            </StyledIconButton>
            <StyledMenu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleClose}
                className='user-bottom-menu'
                disableScrollLock={!isMobile}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleOpenMyAccount}>
                    <EditIcon className='logout-icon' /> {translate('account.myAccount.menuLabel')}
                </MenuItem>
                <MenuItem onClick={handleOpenNotificationSettings}>
                    <EditIcon className='logout-icon' /> {translate('notification.settings.title')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <LogoutIcon className='logout-icon' /> {translate('auth.logout')}
                </MenuItem>
            </StyledMenu>
            <Dialog
                open={isModalOpen}
                onClose={handleDialogClose}
                TransitionComponent={Transition}
                PaperProps={{ className: 'custom-dialog-right custom-dialog-right--no-resizer' }}
            >
                <Box className='custom-dialog-right__inner'>{getModalContent()}</Box>
            </Dialog>
        </>
    );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        top: '49px !important',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: '8px',
        minWidth: '242px',
        [theme.breakpoints.up('md')]: {
            top: '65px !important',
        },
    },
    '& .MuiList-root': {
        padding: '8px 0',
    },
    '& .MuiMenuItem-root': {
        padding: '12px 16px',
        fontSize: '14px',
        '&:hover': {
            backgroundColor: Palette.gray.gray100,
        },
    },
    '& .logout-icon': {
        marginRight: '12px',
    },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    '&.user-menu': {
        width: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.up('md')]: {
            width: 'auto',
            padding: '12px 16px',
        },
        '&.Mui-selected, &:focus, &.active': {
            outline: `2px solid ${Palette.gray.gray900}`,
            outlineOffset: '-2px',
            boxShadow: `0 0 0 2px ${Palette.yellow.yellow400}`,
        },
    },
    '& .user-name': {
        fontSize: '14px',
        fontWeight: '500',
        color: Palette.black,
        marginRight: '8px',
        display: 'none',
        [theme.breakpoints.up('lg')]: {
            display: 'block',
        },
    },
}));

export default CustomUserMenu;
